import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Close, Delete, KeyboardArrowDown, KeyboardArrowUp, MoreVert, RestartAlt } from '@mui/icons-material';
import Tooltip from './Tooltip';
import './DropdownMenu.css';

const SPACE_BUFFER = 8;
const MENU_GAP = 4;

/** listDisplay: 'list' = vertical list | 'icons' = grid icons only | 'gridWithLabels' = grid with label under each icon */
/** triggerIcon: 'chevron' = value selector (default) | 'kebab' = action menu (⋮) | 'delete' = bin icon | 'close' = cross icon | 'reset' = reset icon */
/** item.dividerBefore: draw a rule above this item, to separate groups of actions (ignored on the first item and in grid layouts) */
const DropdownMenu = ({
    items = [],
    menuClassName = '',
    dropdownClassName = '',
    activeIndex: controlledActiveIndex,
    onActiveChange,
    showButtonLabel = true,
    showListLabels = true,
    listDisplay = 'list',
    highlightActiveItem,
    upwards = false,
    children,
    buttonContent,
    buttonTooltip,
    disabled = false,
    triggerIcon = 'chevron',
}) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(controlledActiveIndex ?? 0);
    const [coords, setCoords] = useState(null);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const highlight = highlightActiveItem ?? showButtonLabel;

    useEffect(() => {
        if (controlledActiveIndex !== undefined) {
            setActiveIndex(controlledActiveIndex);
        }
    }, [controlledActiveIndex]);

    useEffect(() => {
        function handleClickOutside(event) {
            const c = containerRef.current;
            const l = listRef.current;
            if (c && !c.contains(event.target) && l && !l.contains(event.target)) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (open && containerRef.current && listRef.current) {
            const calcPos = () => {
                if (!containerRef.current || !listRef.current) return;
                const cr = containerRef.current.getBoundingClientRect();
                const lr = listRef.current.getBoundingClientRect();
                const vw = window.innerWidth;
                const vh = window.innerHeight;

                let vertical = upwards ? 'top' : 'bottom';
                if (!upwards) {
                    const spaceBelow = vh - cr.bottom;
                    const spaceAbove = cr.top;
                    const menuH = lr.height || 200;
                    if (spaceBelow < menuH + SPACE_BUFFER && spaceAbove > spaceBelow) vertical = 'top';
                }

                const minW = parseFloat(getComputedStyle(listRef.current).minWidth) || 120;
                const menuW = Math.max(lr.width || 0, minW);

                let left = cr.left;
                if (left + menuW > vw - SPACE_BUFFER) {
                    const rightAlignedLeft = cr.right - menuW;
                    if (rightAlignedLeft >= SPACE_BUFFER) {
                        left = rightAlignedLeft;
                    } else {
                        left = Math.max(SPACE_BUFFER, vw - SPACE_BUFFER - menuW);
                    }
                }

                const top = vertical === 'bottom' ? cr.bottom + MENU_GAP : undefined;
                const bottom = vertical === 'top' ? vh - cr.top + MENU_GAP : undefined;
                setCoords({ top, bottom, left });
            };

            // Placed now as well as after layout settles: a menu opened while its
            // owner is being redrawn (an overlay re-rendered by the pick before)
            // could lose the deferred pass and stay hidden until a resize.
            calcPos();
            let raf1, raf2, raf3;
            raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => { raf3 = requestAnimationFrame(calcPos); });
            });
            window.addEventListener('resize', calcPos);
            window.addEventListener('scroll', calcPos, true);
            return () => {
                if (raf1) cancelAnimationFrame(raf1);
                if (raf2) cancelAnimationFrame(raf2);
                if (raf3) cancelAnimationFrame(raf3);
                window.removeEventListener('resize', calcPos);
                window.removeEventListener('scroll', calcPos, true);
            };
        } else {
            setCoords(null);
        }
    }, [open, upwards]);

    if (!items.length && !children && !buttonContent) return null;
    const handleMenuItemClick = (item, idx) => {
        if (onActiveChange) onActiveChange(idx);
        if (controlledActiveIndex === undefined) setActiveIndex(idx);
        item.onClick?.();
        setOpen(false);
    };

    const useGrid = listDisplay === 'icons' || listDisplay === 'gridWithLabels';
    const showLabelsUnderIcons = listDisplay === 'gridWithLabels';
    const listClass = `dropdown-menu-list panel glass ${menuClassName}${useGrid ? ' dropdown-menu-list-icons' : ''}${showLabelsUnderIcons ? ' dropdown-menu-list-grid-labels' : ''}`;

    const buttonLabel = buttonContent || (items.length > 0 && items[activeIndex] ? (
        <>
            {items[activeIndex].icon && <span className="dropdown-menu-icon">{items[activeIndex].icon}</span>}
            {showButtonLabel && <span className="dropdown-menu-label">{items[activeIndex].label}</span>}
        </>
    ) : null);

    const buttonElement = (
        <button className="dropdown-menu-button" onClick={() => !disabled && setOpen(o => !o)} disabled={disabled}>
            {buttonLabel}
            {triggerIcon === 'kebab'
                ? <MoreVert className="dropdown-menu-chevron dropdown-menu-kebab" fontSize="small" />
                : triggerIcon === 'delete'
                    ? <Delete className="dropdown-menu-chevron dropdown-menu-delete" fontSize="small" />
                    : triggerIcon === 'close'
                        ? <Close className="dropdown-menu-chevron dropdown-menu-close" fontSize="small" />
                        : triggerIcon === 'reset'
                        ? <RestartAlt className="dropdown-menu-chevron dropdown-menu-reset" fontSize="small" />
                        : open
                    ? <KeyboardArrowUp className="dropdown-menu-chevron" fontSize="small" />
                    : <KeyboardArrowDown className="dropdown-menu-chevron" fontSize="small" />}
        </button>
    );

    return (
        <div className={`dropdown-menu ${dropdownClassName}`} ref={containerRef}>
            {buttonTooltip && !open ? (
                <Tooltip content={buttonTooltip}>
                    {buttonElement}
                </Tooltip>
            ) : buttonElement}
            {open && typeof document !== 'undefined' && createPortal(
                <div
                    className={listClass}
                    ref={listRef}
                    style={{
                        position: 'fixed',
                        top: coords ? (coords.top ?? 'auto') : 0,
                        bottom: coords?.bottom,
                        left: coords ? (coords.left ?? 'auto') : 0,
                        right: undefined,
                        zIndex: 10000,
                        visibility: coords ? 'visible' : 'hidden',
                    }}
                >
                    {children || items.map((item, idx) => {
                        const isDisabled = item.disabled === true;
                        const showLabel = (showListLabels && !useGrid) || showLabelsUnderIcons;
                        const labelCls = showLabelsUnderIcons ? 'dropdown-menu-label dropdown-menu-label-under' : 'dropdown-menu-label';
                        const menuItem = (
                            <div
                                className={`dropdown-menu-item${highlight && idx === activeIndex ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                                onClick={isDisabled ? undefined : () => handleMenuItemClick(item, idx)}
                            >
                                {item.icon && <span className="dropdown-menu-icon">{item.icon}</span>}
                                {showLabel && <span className={labelCls}>{item.label}</span>}
                            </div>
                        );
                        const useTooltip = item.tooltip && !showLabelsUnderIcons;
                        // `dividerBefore` rather than a divider entry of its
                        // own: `idx` is the item's identity here (activeIndex,
                        // onActiveChange), and a spacer in the array would
                        // shift every index after it.
                        const divider = item.dividerBefore && idx > 0 && !useGrid;
                        return (
                            <React.Fragment key={idx}>
                                {divider && <hr className="dropdown-menu-divider" />}
                                {useTooltip ? (
                                    <Tooltip content={item.tooltip}>{menuItem}</Tooltip>
                                ) : menuItem}
                            </React.Fragment>
                        );
                    })}
                </div>,
                document.body
            )}
        </div>
    );
};

export default DropdownMenu;

